<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useBreakpoint } from '@/composables/useBreakpoint';
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Loading from "./Loading.vue";
import StorageService from '@/services/StorageService';

const props = defineProps({
    appendTo: {
        type: String,
        default: 'body',
        description: 'ID do elemento ao qual o Dialog deve se limitar (elemento deve ter position: relative)'
    }
});

// DADOS DOCUMENTO
const documento = ref(null);
const nomeDocumento = ref('');

// DIALOG
const showDialog = ref(false);
const iframepreview = ref(null);
const documentoUrl = ref(null);

// LOADING
const loading = ref(false);
const loadingIframe = ref(false);

// ERRO
const error = ref(false);

// MOBILE/DESKTOP
const { isMobile } = useBreakpoint();
const isMobileDevice = typeof navigator !== 'undefined'
    && /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(navigator.userAgent);
const useMobilePdfFallback = computed(() => isMobile.value || isMobileDevice);

/**
 * Abre Dialog com Iframe para visualizar documento passando ID do Documento no E-storage.
 *
 * @param {number} documentoId - ID do Documento no E-storage
 * @param {string} documentoNome - Nome do Documento
 */
const openDialog = (documentoId, documentoNome = '') => {
    documento.value = null;
    error.value = false;

    showDialog.value = true;
    nomeDocumento.value = documentoNome;

    downloadArquivo(documentoId);
}

/**
 * Abre Dialog com Iframe para visualizar documento passando Base64 do Documento.
 *
 * @param {string} base64 - Base64 do Documento
 * @param {string} documentoNome - Nome do Documento
 */
const openDialogWithBase64 = (base64, documentoNome = '') => {
    documento.value = null;
    error.value = false;

    // Extrai o tipo MIME do Base64
    const matches = base64.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
    let contentType = null;
    let base64Clean = base64;

    if (matches && matches.length > 1) {
        contentType = matches[1];
    } else {
        // Remove prefixo se existir para análise
        base64Clean = base64.split(',')[1] || base64;

        // Tenta detectar o tipo MIME pela assinatura do arquivo
        try {
            const header = atob(base64Clean.substring(0, 8));

            if (header.startsWith('%PDF')) {
                contentType = 'application/pdf';
            } else if (header.startsWith('JVBERi0x')) {
                contentType = 'application/pdf';
            } else if (header.startsWith('\x89PNG')) {
                contentType = 'image/png';
            } else if (header.startsWith('\xFF\xD8\xFF')) {
                contentType = 'image/jpeg';
            } else if (header.startsWith('GIF8')) {
                contentType = 'image/gif';
            } else if (header.startsWith('<svg') || header.startsWith('<?xml')) {
                contentType = 'image/svg+xml';
            }
        } catch (e) {
            console.warn('Não foi possível detectar o MIME type do base64:', e);
        }
    }

    documento.value = {
        documento: base64,
        content_type: normalizeMimeType(contentType, documentoNome)
    }

    nomeDocumento.value = documentoNome;

    showDialog.value = true;
}

const closeDialog = (e) => showDialog.value = false;

const MIME_TYPE_BY_EXTENSION = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
    htm: 'text/html'
};

const getExtensionFromFileName = (fileName = '') => {
    const normalizedFileName = fileName.split('?')[0].trim();
    const extension = normalizedFileName.includes('.') ? normalizedFileName.split('.').pop() : '';

    return extension.toLowerCase();
};

const normalizeMimeType = (mimeType, fileName = '') => {
    if (mimeType && mimeType !== 'application/octet-stream') {
        return mimeType;
    }

    return MIME_TYPE_BY_EXTENSION[getExtensionFromFileName(fileName)] || mimeType || 'application/octet-stream';
};

const revokeDocumentoUrl = () => {
    if (!documentoUrl.value) {
        return;
    }

    URL.revokeObjectURL(documentoUrl.value);
    documentoUrl.value = null;
};

const downloadArquivo = async (documentoId) => {
    loadingIframe.value = true;

    try {
        const resp = await StorageService.obterDocumentoRecenteBase64(documentoId, { 'base64-only-content': false });

        loadingIframe.value = false;

        documento.value = {
            ...resp.data.data,
            content_type: normalizeMimeType(resp.data.data?.content_type, nomeDocumento.value)
        };
    } catch (e) {
        error.value = true;
        loadingIframe.value = false;

        console.error(e);
    }
}

/**
 * Converte Base64 para URL e carrega em um iframe
 *
 * @param {string} base64Data - Dados em Base64 (com ou sem prefixo)
 * @param {string} mimeType - Tipo MIME (ex: 'application/pdf')
 */
function base64ToIframe(base64Data, mimeType) {
    // Remove o prefixo se existir (ex: "data:application/pdf;base64,")
    const base64WithoutPrefix = base64Data.split(',')[1] || base64Data;

    // Converte Base64 para Blob
    const byteCharacters = atob(base64WithoutPrefix);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);

    return blobUrl;
}

const downloadBase64File = (base64, filename, mimeType) => {
    // Remove o prefixo do Base64 se existir (ex: data:image/png;base64,)
    const base64WithoutPrefix = base64.split(';base64,').pop();

    // Converte o Base64 para um array de bytes
    const byteCharacters = atob(base64WithoutPrefix);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Cria um blob com os dados
    const blob = new Blob([byteArray], { type: mimeType });

    // Cria um URL temporário para o blob
    const url = URL.createObjectURL(blob);

    // Cria um elemento <a> temporário para disparar o download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Limpeza
    setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, 100);
}

/** Abre o documento pelo mesmo fluxo já usado pelo botão "Ver em outra aba". */
const abrirNoVisualizadorNativo = () => {
    if (!documentoUrl.value) {
        return;
    }

    window.open(documentoUrl.value, '_blank');
}

const verEmOutraAba = () => {
    abrirNoVisualizadorNativo();
}

const isVisualizavelEmFrame = (text = '') => {
    const patterns = [
        /image\/[^ \n]+/, // Imagens
        /application\/pdf/, // PDFs
        /video\/[^ \n]+/, // Vídeos
        /image\/svg\+xml/, // SVG
        /audio\/[^ \n]+/, // Arquivos de áudio
        /text\/[^ \n]+/, // Textos simples, html, csv, etc.
        /application\/json/, // JSON
        /application\/xml/, // XML
        /application\/[^ \n]+\+xml/ // Outros formatos XML
    ];

    return patterns.some(pattern => pattern.test(text));
};

watch(documento, (novoDocumento) => {
    revokeDocumentoUrl();

    if (!novoDocumento?.documento || !novoDocumento?.content_type || !isVisualizavelEmFrame(novoDocumento.content_type)) {
        return;
    }

    documentoUrl.value = base64ToIframe(novoDocumento.documento, novoDocumento.content_type);

});

onBeforeUnmount(() => {
    revokeDocumentoUrl();
});

defineExpose({ openDialog, openDialogWithBase64, closeDialog });
</script>

<template>
    <Dialog
        header="Visualizador de Documento"
        :modal="true"
        :visible="showDialog"
        :closable="true"
        :append-to="isMobile || props.appendTo === 'body' ? 'body' : `#${props.appendTo}`"
        :pt:mask:style="{ position: isMobile ? 'fixed' : 'absolute' }"
        :pt:root:style="{
            width: isMobile ? '100vw' : '85vw',
            minHeight: isMobile ? '85%' : '90%'
        }"
        :pt:content:style="{ flex: '1', display: 'flex', flexDirection: 'column' }"
        @update:visible="closeDialog"
    >
        <div class="container-visualizador">
            <div v-if="documento" class="actions-container">
                <b
                    v-if="nomeDocumento"
                    :style="{ fontSize: isMobile ? '1.1rem' : '1.3rem', wordBreak: 'break-word' }"
                >
                    {{ nomeDocumento }}
                </b>

                <div class="action-buttons">
                    <Button
                        label="Download"
                        icon="pi pi-download"
                        size="small"
                        @click="downloadBase64File(documento.documento, nomeDocumento, documento.content_type)"
                    />

                    <Button
                        v-if="documento && isVisualizavelEmFrame(documento ? documento.content_type : '')"
                        label="Ver em outra aba"
                        icon="pi pi-eye"
                        size="small"
                        @click="verEmOutraAba"
                    />
                </div>
            </div>

            <div class="documento-preview">
                <Loading :is-loading="loadingIframe" message="Carregando Documento..." />

                <iframe
                    v-if="documento && !useMobilePdfFallback && isVisualizavelEmFrame(documento ? documento.content_type : '')"
                    class="documento-iframe"
                    :src="documentoUrl"
                    width="100%"
                    height="100%"
                    style="flex: 1;"
                    ref="iframepreview"
                    frameborder="0"
                    @load="loadingIframe = false"
                ></iframe>

                <div
                    v-if="documento && useMobilePdfFallback && isVisualizavelEmFrame(documento.content_type)"
                    class="aviso-mobile documento-fallback-mobile"
                >
                    <i class="pi pi-file-pdf" style="font-size: 3rem; color: var(--color-primary);"></i>

                    <p class="text-center">
                        Para visualizar o PDF, toque no botão abaixo.<br>
                        O documento será aberto no visualizador do seu dispositivo.
                    </p>

                    <Button
                        label="Abrir Documento"
                        icon="pi pi-external-link"
                        @click="verEmOutraAba"
                    />
                </div>

                <h1
                    v-if="documento && !isVisualizavelEmFrame(documento ? documento.content_type : '')"
                    class="text-2xl font-bold text-center"
                >
                    Este tipo de arquivo nao pode ser visualizado aqui. Use o botao de download para abrir no aplicativo apropriado.<br>
                </h1>

                <h1 v-if="!loading && error" class="text-2xl font-bold text-center">
                    Não foi possível carregar o documento. Tente novamente mais tarde.
                </h1>
            </div>
        </div>
    </Dialog>
</template>

<style scoped>
.container-visualizador {
    width: 100%;
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
}

.documento-preview {
    height: 100%;
    width: 100%;
    padding: 15px;
    flex-grow: 100;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: scroll;
    overflow-x: auto;
    border: 1px solid var(--color-border);
    flex-direction: column;
    gap: 20px;
}

.actions-container {
    width: 100%;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
}

.aviso-mobile {
    width: 100%;
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 20px;
    text-align: center;
}

</style>
